import React, { useState } from "react";
import { TooltipProvider, Button as JemButton, Tag } from "@jem-open/jem-ui";
import {
  Breadcrumbs,
  Button,
  Checkbox,
  Divider,
  LoadingOverlay,
  Menu,
  Stack,
  Text,
  Center,
  Group,
  Select,
  TextInput,
  Badge,
  createStyles,
  UnstyledButton,
} from "@mantine/core";
import { UserX, MapPin, Search, Clock, ChevronDown, Bell } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { useExceptionCountApiService } from "../services/api/AttendanceExceptionsApiService";
import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import useShiftSiteDetail, {
  StatusFilterValue,
} from "../hooks/useShiftSiteDetail";
import { STATUS_FILTER_DESCRIPTORS } from "../utils/statusFilterConfig";
import EmployeeTimelineRow from "../components/EmployeeTimelineRow";
import EmployeeTable from "../components/EmployeeTable";
import OverrideModal from "../components/OverrideModal";
import SupervisorClockBanner from "../components/SupervisorClockBanner";
import SupervisorClockReviewModal from "../components/SupervisorClockReviewModal";
import ClientBadge from "../elements/ClientBadge";
import { useBreadcrumbStyles } from "../styles/sharedStyles";
import { EmployeeShiftRecord } from "../services/model/EmployeeShiftRecord";
import { useListPendingSubmissions } from "../services/api/SupervisorClockSubmissionApiService";
import AccessGuard from "../../../common/uac/AccessGuard";

const useStyles = createStyles(() => ({
  filterSelect: {
    minWidth: 160,
  },
  filterTrigger: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    width: 220,
    height: 36,
    padding: "0 12px",
    border: "1px solid #ced4da",
    borderRadius: 4,
    backgroundColor: "#fff",
    fontSize: 14,
    fontFamily: "Inter, sans-serif",
    color: "#062133",
    cursor: "pointer",
    overflow: "hidden",
    whiteSpace: "nowrap" as const,
    textOverflow: "ellipsis",
    "&:hover": {
      borderColor: "#adb5bd",
    },
  },
  searchInput: {
    minWidth: 250,
  },
  summaryTag: {
    textTransform: "none" as const,
    fontWeight: 500,
    fontSize: 12,
  },
}));

const ShiftSiteDetailPageInner: React.FC = () => {
  const { classes } = useStyles();
  const { classes: bcClasses } = useBreadcrumbStyles();
  const navigate = useNavigate();
  const { locationId } = useParams<{ locationId: string }>();

  const {
    location,
    filteredRecords,
    isLoading,
    error,
    isTodayView,
    dateLabel,
    shiftConfigs,
    shiftFilter,
    setShiftFilter,
    statusFilters,
    setStatusFilters,
    searchQuery,
    setSearchQuery,
    onTimeCount,
    lateArrivalCount,
    noShowCount,
    onsiteCount,
    earlyCount,
    noDataCount,
    unscheduledCount,
    totalCount,
    coveragePercent,
    lateThreshold,
    isMultiDay,
    dateFilter,
    setDateFilter,
    availableDates,
  } = useShiftSiteDetail();

  const siteExceptionCountQuery = useExceptionCountApiService({
    shift_location: location?.id,
    status: "open",
  });
  const unresolvedSiteExceptionCount = siteExceptionCountQuery.data?.count ?? 0;

  const [overrideEmployee, setOverrideEmployee] =
    useState<EmployeeShiftRecord | null>(null);
  const [statusMenuOpened, setStatusMenuOpened] = useState(false);
  const [pendingFilters, setPendingFilters] =
    useState<StatusFilterValue[]>(statusFilters);

  // Supervisor clock submission approval
  const [reviewSubmissionId, setReviewSubmissionId] = useState<string | null>(
    null
  );
  const { data: pendingSubmissions } = useListPendingSubmissions(
    locationId,
    isTodayView
  );

  // Build shift options
  const shiftOptions = [
    { value: "all", label: "All Shifts" },
    ...shiftConfigs.map((config) => ({
      value: config.id,
      label: config.name,
    })),
  ];

  const statusOptions = STATUS_FILTER_DESCRIPTORS;

  const renderContent = (): JSX.Element => {
    if (error) {
      return (
        <Center style={{ minHeight: 400 }}>
          <Stack align="center" spacing="md">
            <UserX size={48} color="#9CA3AF" />
            <Text color="dimmed">
              Failed to load attendance data. Please try again.
            </Text>
          </Stack>
        </Center>
      );
    }

    if (filteredRecords.length === 0) {
      return (
        <Center style={{ minHeight: 400 }}>
          <Stack align="center" spacing="md">
            <UserX size={64} color="gray" />
            <Text size="lg" color="dimmed">
              No employees found for this site
            </Text>
          </Stack>
        </Center>
      );
    }

    if (isTodayView) {
      return (
        <Stack spacing="md">
          {filteredRecords.map((record) => (
            <EmployeeTimelineRow
              key={`${record.employee_id}-${record.date}-${record.shift_start}-${record.shift_end}`}
              employee={record}
              shiftStart={record.shift_start}
              shiftEnd={record.shift_end}
              lateThreshold={lateThreshold}
              locationLatitude={location?.latitude}
              locationLongitude={location?.longitude}
              locationRadius={location?.radius_allowance}
            />
          ))}
        </Stack>
      );
    }

    return (
      <EmployeeTable records={filteredRecords} showDateColumn={isMultiDay} />
    );
  };

  return (
    <TooltipProvider delayDuration={0}>
      <PageContainer>
        <LoadingOverlay visible={isLoading} />

        <PageHeading title="Time & Attendance" />

        {/* Breadcrumbs */}
        <Breadcrumbs
          separator=">"
          classNames={{
            root: "mt-2 mb-6",
            breadcrumb: "hover:no-underline text-sm",
            separator: "text-gray-400",
          }}
          styles={{
            root: { flexWrap: "wrap", rowGap: 4 },
            breadcrumb: { whiteSpace: "normal", overflowWrap: "anywhere" },
          }}
        >
          <Text
            className={bcClasses.breadcrumbLink}
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/time-attendance")}
          >
            Time &amp; Attendance
          </Text>
          <Text className={bcClasses.breadcrumbCurrent}>
            {location?.name || "..."}
          </Text>
        </Breadcrumbs>

        {/* Header Section */}
        {location && (
          <Stack spacing="xs">
            <Group position="apart" align="flex-start">
              <Group align="center" spacing="sm">
                <Text weight={600} size="lg">
                  {location.name}
                </Text>
                {location.client_name && (
                  <ClientBadge clientName={location.client_name} />
                )}
              </Group>
              <JemButton
                variant="outline"
                onClick={() =>
                  navigate(
                    `/time-attendance/exceptions?site=${encodeURIComponent(
                      location.id
                    )}`
                  )
                }
                className="relative"
              >
                <Bell size={14} className="mr-xxs" />
                View Exceptions
                {unresolvedSiteExceptionCount > 0 && (
                  <Tag
                    variant="failed"
                    size="sm"
                    className="absolute -top-2 -right-2 min-w-[20px] justify-center"
                    aria-label={`${unresolvedSiteExceptionCount} unresolved exceptions`}
                  >
                    {unresolvedSiteExceptionCount}
                  </Tag>
                )}
              </JemButton>
            </Group>
            <Group spacing={4}>
              <MapPin size={16} color="#6B7280" />
              <Text size="xs" color="dimmed">
                {location.region || "No region"}
              </Text>
            </Group>
            <Text size="sm">
              Date:{" "}
              <Text component="span" size="sm" weight={500}>
                {dateLabel}
              </Text>
            </Text>
          </Stack>
        )}

        <Divider my="lg" />

        {/* Search Row */}
        <TextInput
          placeholder="Search employee, on site, tags etc."
          icon={<Search size={16} />}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.currentTarget.value)}
          size="sm"
          className="w-full sm:max-w-[300px]"
        />

        {/* Filters Row */}
        <div className="flex flex-col gap-sm mt-sm sm:flex-row sm:items-center sm:justify-between sm:gap-md">
          <div className="flex flex-col gap-xs sm:flex-row sm:items-center sm:gap-md">
            <Select
              placeholder="Select shift"
              data={shiftOptions}
              value={shiftFilter}
              onChange={(value) => setShiftFilter(value || "all")}
              size="sm"
              className={`${classes.filterSelect} w-full sm:w-auto`}
            />
            <Menu
              opened={statusMenuOpened}
              onOpen={() => {
                setPendingFilters(statusFilters);
                setStatusMenuOpened(true);
              }}
              onClose={() => {
                const sorted = [...pendingFilters].sort();
                const current = [...statusFilters].sort();
                if (JSON.stringify(sorted) !== JSON.stringify(current)) {
                  setStatusFilters(pendingFilters);
                }
                setStatusMenuOpened(false);
              }}
              closeOnItemClick={false}
              withinPortal
              position="bottom"
              placement="start"
              gutter={4}
              styles={{ body: { minWidth: 220 } }}
              control={
                <UnstyledButton className={classes.filterTrigger}>
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {(() => {
                      const active = statusMenuOpened
                        ? pendingFilters
                        : statusFilters;
                      if (active.length === 0) return "All";
                      const selectedLabels = statusOptions
                        .filter((o) =>
                          active.includes(o.value as StatusFilterValue)
                        )
                        .map((o) => o.label);
                      if (selectedLabels.length === 1) return selectedLabels[0];
                      return `${selectedLabels[0]} +${
                        selectedLabels.length - 1
                      }`;
                    })()}
                  </span>
                  <ChevronDown
                    size={16}
                    color="#829099"
                    style={{ flexShrink: 0 }}
                  />
                </UnstyledButton>
              }
            >
              {statusOptions.map((option) => {
                const isSelected = pendingFilters.includes(
                  option.value as StatusFilterValue
                );
                return (
                  <Menu.Item
                    key={option.value}
                    onClick={() => {
                      if (isSelected) {
                        setPendingFilters(
                          pendingFilters.filter((f) => f !== option.value)
                        );
                      } else {
                        setPendingFilters([
                          ...pendingFilters,
                          option.value as StatusFilterValue,
                        ]);
                      }
                    }}
                    icon={
                      <Checkbox
                        checked={isSelected}
                        readOnly
                        size="xs"
                        styles={{
                          input: {
                            cursor: "pointer",
                            borderColor: isSelected ? "#ED7687" : undefined,
                            backgroundColor: isSelected ? "#ED7687" : undefined,
                          },
                        }}
                      />
                    }
                  >
                    {option.label}
                  </Menu.Item>
                );
              })}
            </Menu>
            {isMultiDay && (
              <Select
                placeholder="Specific Date"
                data={[
                  { value: "all", label: "All Dates" },
                  ...availableDates.map((d) => ({
                    value: d,
                    label: dayjs(d).format("D MMMM YYYY"),
                  })),
                ]}
                value={dateFilter}
                onChange={(val) => setDateFilter(val || "all")}
                size="sm"
                className={`${classes.filterSelect} w-full sm:w-auto`}
              />
            )}
          </div>
          <Button
            size="sm"
            onClick={() => navigate(`/time-attendance/${locationId}/clock`)}
            className="w-full sm:w-auto"
          >
            Clock In/Out Employees
          </Button>
        </div>

        {/* Stats Row */}
        <div className="flex flex-col gap-xs mt-md sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-lg">
          <div className="flex flex-wrap items-center gap-x-md gap-y-xs">
            {isTodayView && (
              <>
                <Text>
                  <Text component="span" weight={700}>
                    {onsiteCount}
                  </Text>{" "}
                  of{" "}
                  <Text component="span" weight={700}>
                    {totalCount}
                  </Text>{" "}
                  Onsite
                </Text>
                <Text color="dimmed" className="hidden sm:block">
                  |
                </Text>
              </>
            )}
            <Group spacing={6}>
              <Clock size={16} color="#6B7280" />
              <Text size="sm" color="dimmed">
                Coverage:{" "}
                {coveragePercent !== null ? Math.round(coveragePercent) : 0}% (
                {onsiteCount}/{totalCount})
              </Text>
            </Group>
          </div>

          {/* Summary Count Tags (display-only) */}
          {isTodayView && (
            <div className="flex flex-wrap items-center gap-xxs">
              <Group spacing={6}>
                <Badge
                  size="lg"
                  variant="outline"
                  className={classes.summaryTag}
                  style={{
                    color: "#14926D",
                    backgroundColor: "#E8FAF5",
                    border: "1px solid #B7EBD8",
                  }}
                >
                  Early: {earlyCount}
                </Badge>
                <Badge
                  size="lg"
                  variant="outline"
                  className={classes.summaryTag}
                  style={{
                    color: "#14926D",
                    backgroundColor: "#E8FAF5",
                    border: "1px solid #B7EBD8",
                  }}
                >
                  On Time: {onTimeCount}
                </Badge>
                <Badge
                  size="lg"
                  variant="outline"
                  className={classes.summaryTag}
                  style={{
                    color: "#D97706",
                    backgroundColor: "#FFF8E1",
                    border: "1px solid #FFE082",
                  }}
                >
                  Late Arrival: {lateArrivalCount}
                </Badge>
                <Badge
                  size="lg"
                  variant="outline"
                  className={classes.summaryTag}
                  style={{
                    color: "#6B7280",
                    backgroundColor: "#F3F4F6",
                    border: "1px solid #D1D5DB",
                  }}
                >
                  No data: {noDataCount}
                </Badge>
                <Badge
                  size="lg"
                  variant="outline"
                  className={classes.summaryTag}
                  style={{
                    color: "#C53030",
                    backgroundColor: "#FFF5F5",
                    border: "1px solid #FEB2B2",
                  }}
                >
                  No Show: {noShowCount}
                </Badge>
                {unscheduledCount > 0 && (
                  <Badge
                    size="lg"
                    variant="outline"
                    className={classes.summaryTag}
                    style={{
                      color: "#6D28D9",
                      backgroundColor: "#F5F3FF",
                      border: "1px solid #DDD6FE",
                    }}
                  >
                    Unscheduled: {unscheduledCount}
                  </Badge>
                )}
              </Group>
            </div>
          )}
        </div>

        {/* Supervisor Clock Submission Banners */}
        {isTodayView && pendingSubmissions && pendingSubmissions.length > 0 && (
          <Stack spacing="sm" mt="md">
            {pendingSubmissions.map((submission) => (
              <SupervisorClockBanner
                key={submission.id}
                submission={submission}
                onViewRequest={() => setReviewSubmissionId(submission.id)}
              />
            ))}
          </Stack>
        )}

        {/* Content Area */}
        <div className="mt-4" style={{ minHeight: 400 }}>
          {renderContent()}
        </div>

        {/* Override Modal */}
        {overrideEmployee && location && (
          <OverrideModal
            opened
            onClose={() => setOverrideEmployee(null)}
            employee={overrideEmployee}
            locationId={location.id}
          />
        )}

        {/* Supervisor Clock Review Modal */}
        <SupervisorClockReviewModal
          opened={reviewSubmissionId !== null}
          submissionId={reviewSubmissionId}
          locationId={locationId}
          onClose={() => setReviewSubmissionId(null)}
        />
      </PageContainer>
    </TooltipProvider>
  );
};

const ShiftSiteDetailPage: React.FC = () => (
  <AccessGuard>
    <ShiftSiteDetailPageInner />
  </AccessGuard>
);

export default ShiftSiteDetailPage;
